import "./Subcontractor.scss";
import { FormattedMessage } from "react-intl";
import { Typography } from "core/typography/Typography";
import { Controller } from "utils/Controller";
import InputTextArea from "core/inputs/input-text-area/InputTextArea";
import {
  STAFF_FORM_PLACEHOLDERS,
  SUBCONTRACTOR_FORM_LABELS,
  SUBCONTRACTOR_FORM_PLACEHOLDERS,
} from "constants/forms";
import { useContext, useEffect, useMemo } from "react";
import {
  renderInputPicture,
  renderPhonenumber,
  renderFirstName,
  renderLastName,
  renderInputEmail,
  renderInputGender,
  renderInputStrCompanyName,
  renderInputSiren,
} from "../CommonInputs";
import { INPUTS_IDS } from "constants/constants";
import { tabletSize } from "utils/screenSizes";
import { useParams } from "react-router-dom";
import PropTypes from "prop-types";
import InfoContext from "pages/InfoContext";

const SubcontractorPersonalInfoForm = ({
  control,
  register,
  setValue,
  watch,
  errors,
  readOnly = false,
}) => {
  const slug = useParams().slug;
  const gender = watch("gender", "");
  const {
    cellHighlight,
    picture,
    createFile,
    uploadProfilePicture,
    createdFile,
    setCreatedFile,
    setCode,
    subcontractorBySlug,
    setStatus,
    apiErrorResponse,
    tempUserId,
  } = useContext(InfoContext);

  // Memoize picture value to prevent unnecessary re-renders
  const pictureValue = useMemo(() => {
    return picture || createdFile?.uri || createdFile?.picture_file_id || "";
  }, [picture, createdFile?.uri, createdFile?.picture_file_id]);

  useEffect(() => {
    setValue("company_name", subcontractorBySlug?.subcontractor_company_name);
    setValue("siren", subcontractorBySlug?.subcontractor_company_siren);
  }, [subcontractorBySlug]);

  useEffect(() => {
    setValue(
      "picture_file_id",
      createdFile?.picture_file_id || createdFile?.uri
    );
  }, [createdFile]);

  const renderInputCompanyAssignment = () => (
    <Controller
      {...{
        control,
        register,
        name: "company_comment",
        render: (props) => (
          <InputTextArea
            errors={
              errors["company_comment"] || apiErrorResponse?.["company_comment"]
            }
            label={SUBCONTRACTOR_FORM_LABELS.company_comment}
            id="company_comment"
            setValue={setValue}
            width={tabletSize ? "100%" : 310}
            {...props}
          />
        ),
      }}
    />
  );

  return (
    <div className="section-1 subcontractor-personalInfo-form-wrapper">
      <div>
        <div className="title_block">
          <Typography
            className="title-3 violet text-uppercase"
            type="h2"
            label={
              <FormattedMessage
                id="who_s_she_he"
                defaultMessage="WHO'S SHE/HE ?"
              />
            }
          />
        </div>
        <div
          className="subcontractor_personalinfo"
          id="subcontractor_personalinfo"
        >
          {!slug && (
            <div className="corner-left">
              {renderInputPicture({
                label: SUBCONTRACTOR_FORM_LABELS.picture_file_id,
                value: pictureValue,
                readOnly,
                id: INPUTS_IDS.SUBCONTRACTOR,
                createFile,
                uploadProfilePicture,
                setCreatedFile,
                setCode,
                setStatus,
                userId: tempUserId,
                createdFile,
              })}
            </div>
          )}
          <div className="center-block">
            <div className="line-1">
              <div
                className={`cell-padding ${
                  (!gender && cellHighlight) ||
                  errors?.["gender"] ||
                  apiErrorResponse?.["gender"]
                    ? "errorCellColor"
                    : ""
                } `}
                style={{ borderRadius: slug ? "6px 0 0 0" : "0" }}
              >
                {renderInputGender({
                  control,
                  errors,
                  label: SUBCONTRACTOR_FORM_LABELS.gender,
                  placeholder: SUBCONTRACTOR_FORM_LABELS.placeholder,
                  readOnly,
                  register,
                  value: gender,
                  setValue,
                  errorMessage: apiErrorResponse?.["gender"],
                })}
              </div>
              <div
                className={`cell-padding ${
                  (!watch("first_name") && cellHighlight) ||
                  errors?.["first_name"] ||
                  apiErrorResponse?.["first_name"]
                    ? "errorCellColor"
                    : ""
                } `}
              >
                {renderFirstName({
                  control,
                  errors,
                  label: SUBCONTRACTOR_FORM_LABELS.first_name,
                  placeholder: SUBCONTRACTOR_FORM_LABELS.first_name,
                  readOnly,
                  register,
                  errorMessage: apiErrorResponse?.["first_name"],
                })}
              </div>
              <div
                className={`cell-padding ${
                  (!watch("last_name") && cellHighlight) ||
                  errors?.["last_name"] ||
                  apiErrorResponse?.["last_name"]
                    ? "errorCellColor"
                    : ""
                } `}
              >
                {renderLastName({
                  control,
                  errors,
                  label: SUBCONTRACTOR_FORM_LABELS.last_name,
                  placeholder: SUBCONTRACTOR_FORM_LABELS.last_name,
                  readOnly,
                  register,
                  errorMessage: apiErrorResponse?.["last_name"],
                })}
              </div>
            </div>
            <div className="line-2">
              <div
                className={`cell-padding ${
                  (errors["email"] || apiErrorResponse?.["email"]) &&
                  "errorCellColor"
                }`}
              >
                {renderInputEmail({
                  control,
                  errors,
                  label: SUBCONTRACTOR_FORM_LABELS.personal_email,
                  placeholder: SUBCONTRACTOR_FORM_LABELS.personal_email,
                  readOnly,
                  register,
                  errorMessage:
                    apiErrorResponse?.["email"] || errors?.["email"]?.message,
                  copyEmail: slug,
                  setCode,
                  setStatus,
                })}
              </div>
              <div
                className={`cell-padding ${
                  (!watch("phone_number") && cellHighlight) ||
                  errors?.["phone_number"] ||
                  apiErrorResponse?.["phone_number"]
                    ? "errorCellColor"
                    : ""
                } `}
                style={{ borderRadius: "0 0 6px 0" }}
              >
                {renderPhonenumber({
                  control,
                  errors,
                  label: SUBCONTRACTOR_FORM_LABELS.phone_number,
                  placeholder: STAFF_FORM_PLACEHOLDERS.phone_number,
                  readOnly,
                  register,
                  required: true,
                  errorMessage:
                    apiErrorResponse?.["phone_number"] ||
                    (watch("phone_number") &&
                      errors?.["phone_number"]?.message),
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="subcontractor_company_comment">
        <Typography
          className="title-3 violet text-uppercase"
          type="h2"
          label={
            <FormattedMessage
              id="company_comment"
              defaultMessage="ASSIGNATION DE SOCIÉTÉ"
            />
          }
        />
        <div
          className="renderCAParent"
          style={{ height: !slug ? "163px" : "" }}
        >
          {slug ? (
            <div className="corner-left">
              <div className="line-2">
                <div className="cell-padding">
                  {renderInputStrCompanyName({
                    control,
                    register,
                    label: SUBCONTRACTOR_FORM_LABELS.company_name,
                    placeholder: SUBCONTRACTOR_FORM_PLACEHOLDERS.company_name,
                    readOnly: true,
                  })}
                </div>
                <div className="cell-padding">
                  {renderInputSiren({
                    control,
                    errors,
                    setValue,
                    label: SUBCONTRACTOR_FORM_LABELS.siren,
                    placeholder: SUBCONTRACTOR_FORM_PLACEHOLDERS.siren,
                    readOnly: true,
                    register,
                    required: false,
                  })}
                </div>
              </div>
            </div>
          ) : (
            <div className="corner-left">
              <div className="line-1" style={{ height: "160px!important" }}>
                <div className="cell-padding add-str">
                  {renderInputCompanyAssignment()}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
export default SubcontractorPersonalInfoForm;

SubcontractorPersonalInfoForm.propTypes = {
  control: PropTypes.object,
  register: PropTypes.func,
  setValue: PropTypes.func,
  watch: PropTypes.func,
  errors: PropTypes.object,
  readOnly: PropTypes.bool,
};
